import { Box, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-pro';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import BaseDataGrid from 'components/shared/baseDataGrid/BaseDataGrid';
import GeneratedForm from 'components/shared/generatedForm/GeneratedForm';
import { useGetMarketplaceOrdersQuery } from 'store/apis/MarketplaceApi';
import { MarketplaceOrderFields } from './form.constants';
import { MarketplaceOrderDetail } from './MarketplaceOrderDetail';
import { CustomNoRowsOverlay } from 'components/admin/admin.overlays';

export default function MarketplaceSubmitted() {
  const currentUserData = useOutletContext();
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
  const [committedParams, setCommittedParams] = useState(null);

  const methods = useForm({
    shouldUnregister: false,
    mode: 'all',
    defaultValues: {
      bistroMenuId: null,
      startDate: dayjs(),
      endDate: dayjs(),
    },
  });

  const { watch, control, setValue, getValues, formState: { errors } } = methods;
  const bistroMenuId = watch('bistroMenuId');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  useEffect(() => {
    if (!currentUserData?.facilityId) return;
    const saved = localStorage.getItem('lastMarketplaceId');
    if (saved) {
      const menuId = Number(saved);
      setValue('bistroMenuId', menuId);
      const { startDate: sd, endDate: ed } = getValues();
      setCommittedParams({
        facilityId: currentUserData?.facilityId,
        bistroMenuId: menuId,
        startDate: dayjs(sd).format('YYYY-MM-DD'),
        endDate: dayjs(ed).format('YYYY-MM-DD'),
      });
    }
  }, [currentUserData?.facilityId]);

  useEffect(() => {
    if (!bistroMenuId) return;
    localStorage.setItem('lastMarketplaceId', bistroMenuId);
  }, [bistroMenuId]);

  const canSearch = !!currentUserData?.facilityId && !!bistroMenuId && !!startDate && !!endDate;

  const handleSearch = () => {
    if (!canSearch) return;
    setCommittedParams({
      facilityId: currentUserData?.facilityId,
      bistroMenuId,
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
    });
  };

  const { data: orders, isFetching } = useGetMarketplaceOrdersQuery(
    committedParams,
    { skip: !committedParams, refetchOnMountOrArgChange: true }
  );

  const queries = [
    { name: 'bistroMenuId', query: currentUserData?.facilityId, skip: !currentUserData?.facilityId },
  ];

  const columns = [
    {
      field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
      headerName: '',
      minWidth: 50,
      maxWidth: 50,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          onClick={() =>
            setDetailPanelExpandedRowIds(
              detailPanelExpandedRowIds?.at(0) === params.id ? [] : [params.id]
            )
          }
        >
          <ExpandIcon
            sx={{
              fontSize: '1.75rem !important',
              transform: detailPanelExpandedRowIds.includes(params.id)
                ? 'rotate(180deg)'
                : '',
              transition: '.3s all',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'residentName',
      headerName: 'Resident',
      flex: 1.25,
      minWidth: 150,
      renderCell: (params) => {
          const name = params?.row?.residentId 
            ? params?.row?.residentName 
            : params?.row?.guestName;
        return (
          <span>{name}</span>
        );
      }
    },
    {
      field: 'price',
      headerName: 'Total',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => `$${params.value?.toFixed(2)}`,
    },
    {
      field: 'billingTypeId',
      headerName: 'Billed To',
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) =>
        params.value === 1 ? 'Card' : params.value === 2 ? 'Resident Account' : 'External Payment',
    },
    {
      field: 'createdDate',
      headerName: 'Date',
      flex: 0,
      minWidth: 175,
      renderCell: (params) => dayjs(params.value).format("M/D/YY h:mm A"),
    },
  ];

  const getDetailPanelContent = ({ row }) => <MarketplaceOrderDetail row={row} />;

  return (
    <div className="paperContent customScrollbar">
      <Box width="100%" padding="0 1rem">
        <FormProvider {...methods}>
          <Grid container spacing={2} sx={{ alignItems: 'flex-end', marginBottom: '1rem' }}>
            <GeneratedForm list={MarketplaceOrderFields} control={control} errors={errors} queries={queries} />
            <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignSelf: 'center' }}>
              <Button
                variant="contained"
                fullWidth
                disabled={!canSearch}
                size="small"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ marginTop: ".25rem"}}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </FormProvider>
        {!committedParams && (
          <Typography variant="h6" textAlign="center" mt={4} color="text.secondary">
            {!bistroMenuId
              ? 'Please select a marketplace to view orders'
              : 'Select a date range and click Search'}
          </Typography>
        )}
        {committedParams && (
          <BaseDataGrid
            rows={orders || []}
            loading={isFetching}
            columns={columns}
            height="60vh"
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={() => 'auto'}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            rowHeight={60}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            onRowClick={(params) =>
              setDetailPanelExpandedRowIds(
                detailPanelExpandedRowIds?.at(0) === params.id ? [] : [params.id]
              )
            }
            sx={{
              '.MuiDataGrid-row:hover': { cursor: 'pointer', backgroundColor: 'lightgray' },
            }}
          />
        )}
      </Box>
    </div>
  );
}